import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router';
import { CurrencyFormattedDisplay, Grid, ServerDataGrid } from '@emburse/embark';
import { CellParams } from '@material-ui/data-grid';

import { dateTimeFormatter } from '../../helpers/datagrid_formatters';
import { ExpenseState, ISerializedExpense } from '../../types/api/serialized_expense';
import { useApi, usePagination } from '../../hooks';
import { ListExpensesWhere, listUserExpensesForCompany } from '../../api/routes/expenses';
import { EXPENSES_PER_PAGE, PER_PAGE_OPTIONS } from '../../constants';
import { Spacer } from '../Common/UI';
import { FilterBar } from '../Common/FilterBar';
import { SearchBar } from '../Common/SearchBar/SearchBar';

interface IUserExpensesProps { currency: string }
interface PageRouteParams { companyId: string, userId: string }

const stateOptions: { label: string; value: ExpenseState; }[] = [
  { label: 'New', value: 'new' },
  { label: 'Approved', value: 'approved' },
  { label: 'Denied', value: 'denied' },
  { label: 'Approved By Manager', value: 'manager_approved' },
  { label: 'Paid', value: 'paid' }
];

function UserExpensesList({ currency }: IUserExpensesProps) {
  const [where, setWhere] = useState<ListExpensesWhere>({});
  const { companyId, userId } = useParams<PageRouteParams>();
  const { limit, offset, onPageChange } = usePagination(EXPENSES_PER_PAGE);
  const { items, loading } = useApi<ISerializedExpense>(listUserExpensesForCompany(companyId, userId, limit, offset, where));

  const expenseColumns =  useMemo(() => ([
    { field: 'id', hide: true },
    { field: 'company_id', hide: true },
    { field: 'user_id', hide: true },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 100,
      sortable: false,
      renderCell: function render(params: CellParams) {
        const amount = params.getValue('amount') as number / 100;
        const expenseId: string = params.getValue('id') as string;
        return (
          <Link to={`/companies/${companyId}/users/${userId}/expenses/${expenseId}`}>
            <CurrencyFormattedDisplay amount={amount} code={currency} />
          </Link>
        );
      }
    },
    { field: 'created_at', headerName: 'Create Date', width: 200, sortable: false, valueFormatter: dateTimeFormatter },
    { field: 'date', headerName: 'Expense Date', width: 200, sortable: false, valueFormatter: dateTimeFormatter },
    { field: 'note', headerName: 'Note', sortable: false, width: 300 },
    { field: 'vendor', headerName: 'Vendor', sortable: false, width: 300 },
    { field: 'state', headerName: 'State', sortable: false }
  ]), [currency, companyId, userId]);

  const handleSearch = (searchQuery: string) => {
    onPageChange(0, limit);
    setWhere({ ...where, noteOrVendor: searchQuery });
  };

  const handleFilter = (state: string) => {
    onPageChange(0, limit);
    setWhere({ ...where, state });
  };

  return (
    <>
      <Spacer />
      <Grid container justify="space-between" spacing={2}>
        <Grid item justify="flex-start">
          <SearchBar onSearch={handleSearch} label="Note/Vendor"/>
        </Grid>
        <Grid item>
          <FilterBar label="Filter by state" options={stateOptions} onFilter={handleFilter} />
        </Grid>
      </Grid>
      <Spacer />
      <ServerDataGrid
        rowCount={items.count}
        rows={items.rows}
        columns={expenseColumns}
        autoHeight={true}
        rowsPerPageOptions={PER_PAGE_OPTIONS}
        pageSize={limit}
        loading={loading}
        onPageChange={onPageChange}
      />
    </>

  );
}

export { UserExpensesList };
